'use client';

import React, { RefObject } from 'react';
import { useTranslations } from 'next-intl';
import { StyledButton, StyledSpinner } from './Button.styles';
import Icon from '@atoms/Icon/Icon';
import variables from '@styles/variables';

export interface ButtonProps extends Pick<
  React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  | 'children'
  | 'disabled'
  | 'onClick'
  | 'onKeyDown'
  | 'tabIndex'
  | 'className'
  | 'type'
  | 'title'
  | 'onKeyUp'
  | 'onFocus'
  | 'onBlur'
> {
  size?: ButtonSize;
  variant?: ButtonTheme;
  color?: string;
  ariaExpanded?: boolean;
  background?: string;
  borderColor?: string;
  'data-testid'?: string;
  'aria-label'?: string;
  centered?: boolean;
  isSubmitting?: boolean;
  isSelected?: boolean;
  fullWidth?: boolean;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  ref?: RefObject<HTMLButtonElement | null>;
}

const Button = ({
  variant = 'primary',
  disabled = false,
  onClick,
  onKeyDown,
  children,
  size = 'default',
  title,
  type = 'button',
  color,
  background,
  borderColor,
  className,
  centered = false,
  isSubmitting,
  isSelected,
  onFocus,
  onBlur,
  ariaExpanded,
  fullWidth,
  icon,
  ref,
  ...props
}: ButtonProps) => {
  const t = useTranslations('common');

  const whiteSpinnerThemes: Array<ButtonTheme> = ['primary', 'primary-light-bg'];
  const spinnerColor: keyof typeof variables.colors = whiteSpinnerThemes.includes(variant) ? 'white' : 'black';

  const buttonContent = isSubmitting ? (
    <>
      <StyledSpinner title={t('button.submitting')} color={spinnerColor} size={size?.includes('small') ? 16 : 20} />
      <span>{children}</span>
    </>
  ) : (
    <>
      {icon && <Icon svg={icon} size={20} customColor="currentColor" />}
      {children}
    </>
  );

  return (
    <StyledButton
      ref={ref}
      data-testid={props['data-testid']}
      className={className}
      $variant={variant}
      $background={background}
      aria-expanded={ariaExpanded}
      $color={color}
      $size={size}
      $centered={centered}
      type={type}
      title={title}
      onClick={onClick}
      onKeyDown={onKeyDown}
      disabled={disabled || isSubmitting}
      aria-disabled={disabled || isSubmitting}
      $isSubmitting={isSubmitting}
      $borderColor={borderColor}
      aria-label={isSubmitting ? t('button.submitting') : props['aria-label']}
      $isSelected={isSelected}
      onFocus={onFocus}
      onBlur={onBlur}
      $fullWidth={fullWidth}
    >
      {buttonContent}
    </StyledButton>
  );
};

export default Button;
